import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Target, BedDouble, Receipt, Building2, Shield, Eye, MapPin, Ticket } from 'lucide-react';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';

const Section = ({ icon: Icon, title, children }) => (
  <section>
    <h2 className="text-xl md:text-2xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
      <Icon className="h-5 w-5 text-[#1e3a5f] shrink-0" />
      {title}
    </h2>
    {children}
  </section>
);

const AboutUs = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="about-us-page">
      <SEO title="About StayMii | Hospitality Technology Platform" description="StayMii is a modern hospitality technology platform for hotels, serviced apartments, and accommodation providers. Cloud-based PMS, e-Invoice, and e-Ticket solutions." canonicalUrl="/about" />
      <LandingNavBar />

      <div className="bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] pt-28 pb-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Globe className="h-10 w-10 text-white mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3" data-testid="about-title">About StayMii</h1>
          <p className="text-white/70 text-base max-w-2xl mx-auto leading-relaxed">
            Modern hospitality technology designed to help hotels, serviced apartments, and accommodation providers simplify operations and grow their businesses.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 md:p-8 space-y-10">

          {/* Team Image */}
          <section>
            <img 
              src="https://customer-assets.emergentagent.com/job_a6a0f043-1b5a-4683-861b-014c5cde6c7c/artifacts/p1azp4tc_generate_images_for_StayMii_about_us_for_about_us_page_with_asian_team.jpeg" 
              alt="StayMii Team" 
              className="w-full h-auto rounded-xl shadow-md mb-6"
            />
          </section>

          <section>
            <p className="text-slate-600 leading-relaxed mb-4">
              StayMii is a modern hospitality technology platform designed to help hotels, serviced apartments, and accommodation providers simplify their operations and grow their businesses.
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              Built with the needs of today's hospitality operators in mind, StayMii provides powerful cloud-based tools that streamline property management, automate workflows, and support regulatory compliance.
            </p>
            <p className="text-slate-600 leading-relaxed">
              StayMii currently offers three core products: <strong>StayMii Lite</strong> — an optional lightweight operations tool for small operators without a PMS; <strong>StayMii e-Invoice</strong> — an e-invoicing and compliance platform that integrates with your existing PMS; and <strong>StayMii e-Ticket</strong> — a ticketing and OTA distribution module for attractions, tours, and event operators.
            </p>
          </section>

          <Section icon={Target} title="Our Mission">
            <p className="text-slate-600 leading-relaxed mb-4">
              Our mission is to empower hospitality businesses with reliable, secure, and easy-to-use technology that simplifies operations and improves efficiency.
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              We believe hotel technology should be accessible, flexible, and built around the real needs of hospitality operators.
            </p>
            <p className="text-slate-600 leading-relaxed">
              StayMii is designed to help property owners and managers focus on delivering great guest experiences while the platform handles the operational complexity behind the scenes.
            </p>
          </Section>

          <Section icon={BedDouble} title="StayMii Lite">
            <p className="text-slate-600 leading-relaxed mb-4">
              StayMii Lite is an optional lightweight operations tool designed for small operators without an existing PMS. It helps manage day-to-day hospitality operations.
            </p>
            <p className="text-slate-600 leading-relaxed mb-3">StayMii Lite helps operators manage:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1.5 ml-2">
              <li>Booking tracking</li>
              <li>Guest records and check-ins</li>
              <li>Basic calendar and availability</li>
              <li>Payment tracking</li>
              <li>Operational reports</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              StayMii Lite is fully integrated with StayMii's e-invoicing system, ensuring small operators stay compliant with LHDN requirements.
            </p>
          </Section>

          <Section icon={Receipt} title="StayMii e-Invoice">
            <p className="text-slate-600 leading-relaxed mb-4">
              StayMii e-Invoice is a digital invoicing solution designed to support businesses in generating and managing electronic invoices.
            </p>
            <p className="text-slate-600 leading-relaxed mb-3">
              The system is designed to align with Malaysia's electronic invoicing framework administered by Lembaga Hasil Dalam Negeri Malaysia through the MyInvois platform.
            </p>
            <p className="text-slate-600 leading-relaxed mb-3">StayMii e-Invoice allows businesses to:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1.5 ml-2">
              <li>Generate compliant invoice data</li>
              <li>Maintain digital transaction records</li>
              <li>Integrate billing workflows with StayMii Lite</li>
              <li>Support tax reporting requirements</li>
            </ul>
          </Section>

          <Section icon={Ticket} title="StayMii e-Ticket">
            <p className="text-slate-600 leading-relaxed mb-4">
              StayMii e-Ticket is a ticketing and OTA distribution module designed for attractions, tours, cultural venues, and event operators — helping them sell tickets directly and through connected online travel agents.
            </p>
            <p className="text-slate-600 leading-relaxed mb-3">StayMii e-Ticket helps operators:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1.5 ml-2">
              <li>Create and price ticketed products and time-slotted sessions</li>
              <li>Sell tickets directly and through connected OTA distribution partners</li>
              <li>Issue QR-coded electronic tickets to buyers by email</li>
              <li>Scan and validate tickets on-site with a signed audit trail</li>
              <li>Reconcile ticket sales, refunds, and OTA payouts</li>
              <li>Comply with LHDN e-invoice requirements via StayMii e-Invoice</li>
            </ul>
          </Section>

          <Section icon={Building2} title="Built for Modern Hospitality">
            <p className="text-slate-600 leading-relaxed mb-3">StayMii is designed to support a wide range of hospitality and experience businesses including:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1.5 ml-2">
              <li>Boutique hotels</li>
              <li>Serviced apartments</li>
              <li>Short-stay accommodations</li>
              <li>Attractions, tours, and cultural venues</li>
              <li>Event and activity operators</li>
              <li>Hospitality operators managing multiple properties</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              The platform is built using modern cloud technologies to ensure reliability, scalability, and accessibility.
            </p>
          </Section>

          <Section icon={Shield} title="Security and Compliance">
            <p className="text-slate-600 leading-relaxed mb-3">
              StayMii is developed with strong attention to security and regulatory compliance. The platform supports:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-1.5 ml-2">
              <li>Secure cloud infrastructure</li>
              <li>Encrypted data transmission</li>
              <li>Data protection practices aligned with Malaysia's PDPA</li>
              <li>Compliance support for the MyInvois electronic invoicing framework</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              These measures help hospitality operators protect guest data and maintain operational trust.
            </p>
          </Section>

          <Section icon={Eye} title="Our Vision">
            <p className="text-slate-600 leading-relaxed mb-4">
              StayMii aims to become a trusted hospitality technology platform across Malaysia and the wider Southeast Asian region.
            </p>
            <p className="text-slate-600 leading-relaxed">
              By combining modern software design with deep understanding of hospitality operations, StayMii strives to provide technology that enables accommodation providers to operate more efficiently and serve their guests better.
            </p>
          </Section>

          <Section icon={MapPin} title="Company Information">
            <div className="bg-slate-50 rounded-lg p-5">
              <p className="text-slate-800 font-semibold text-lg mb-1">StayMii</p>
              <p className="text-slate-600 text-sm mb-3">Powered by Mii Global Partner Holdings Sdn Bhd (202601028203 (1690298-K))</p>
              <p className="text-slate-500 text-sm">Malaysia | Singapore | International</p>
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-slate-600 text-sm">11, Taman Bukit Dumbar</p>
                <p className="text-slate-600 text-sm">11600 Jelutong, Penang, Malaysia</p>
                <p className="text-sm mt-2">
                  Email: <a href="mailto:support@staymii.com" className="text-[#2563EB] hover:underline">support@staymii.com</a>
                </p>
                <p className="text-sm">
                  Website: <a href="https://staymii.com" className="text-[#2563EB] hover:underline">https://staymii.com</a>
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-slate-500 text-xs italic">
                  StayMii is a proprietary software with registered copyright protection in Malaysia.
                </p>
              </div>
            </div>
          </Section>
        </div>
      </div>

      <LandingFooter />
    </div>
  );
};

export default AboutUs;
